import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

function parseJson(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch {}
  const m = raw.match(/\{[\s\S]*\}/);
  if (m) { try { return JSON.parse(m[0]); } catch {} }
  return null;
}

export default function Competitors() {
  const { activeBiz } = useOutletContext();
  const [keyword, setKeyword] = useState("");
  const [data, setData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisRaw, setAnalysisRaw] = useState("");
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    if (!activeBiz) return;
    const first = (activeBiz.keywords || [])[0];
    if (first) { setKeyword(first); load(first); }
    // eslint-disable-next-line
  }, [activeBiz]);

  const load = async (kw) => {
    if (!activeBiz || !kw) return;
    setLoading(true); setAnalysis(null); setAnalysisRaw("");
    try {
      const { data } = await api.get(`/businesses/${activeBiz.id}/competitors`, { params: { keyword: kw } });
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };

  const analyze = async () => {
    if (!activeBiz || !keyword) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/ai/competitor-analysis`, { keyword });
      setAnalysisRaw(data.raw);
      const p = parseJson(data.raw);
      if (p) setAnalysis(p); else toast.error("AI returned non-JSON, see raw.");
    } catch (e) { toast.error(formatErr(e)); }
    setAnalyzing(false);
  };

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-6xl">
      <header>
        <div className="label-tiny mb-1">Competitor Spy</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="comp-title">Reverse-engineer the Top 3</h1>
        <p className="text-zinc-500 mt-1">See exactly what the businesses ranking above you are doing right.</p>
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="label-tiny">Keyword:</span>
        {(activeBiz.keywords || []).map((k) => (
          <button
            key={k}
            onClick={() => { setKeyword(k); load(k); }}
            className={`px-3 py-1.5 text-xs font-mono border ${keyword === k ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200 hover:border-zinc-400"}`}
            data-testid={`comp-kw-${k}`}
          >
            {k}
          </button>
        ))}
      </div>

      {loading && <div className="py-12 flex justify-center"><span className="spinner text-zinc-400"></span></div>}

      {data && !loading && (
        <>
          <div className="bento p-0 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="label-tiny p-4 text-left">#</th>
                  <th className="label-tiny p-4 text-left">Business</th>
                  <th className="label-tiny p-4 text-left">Rating</th>
                  <th className="label-tiny p-4 text-left">Reviews</th>
                  <th className="label-tiny p-4 text-left">Photos</th>
                  <th className="label-tiny p-4 text-left">Posts/30d</th>
                  <th className="label-tiny p-4 text-left">Distance</th>
                </tr>
              </thead>
              <tbody>
                {data.competitors.map((c) => (
                  <tr key={c.rank} className="border-b border-zinc-100" data-testid={`comp-row-${c.rank}`}>
                    <td className="p-4 font-mono font-bold">#{c.rank}</td>
                    <td className="p-4 font-medium">{c.name}</td>
                    <td className="p-4 font-mono text-sm">{c.rating} ★</td>
                    <td className="p-4 font-mono text-sm">{c.review_count}</td>
                    <td className="p-4 font-mono text-sm">{c.photos}</td>
                    <td className="p-4 font-mono text-sm">{c.posts_last_30d}</td>
                    <td className="p-4 font-mono text-sm text-zinc-500">{c.address_distance_mi}mi</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-3">
            <button className="btn-primary" onClick={analyze} disabled={analyzing} data-testid="comp-analyze">
              {analyzing ? <span className="spinner"></span> : <><Sparkle size={14} weight="fill" /> AI Strategy Analysis</>}
            </button>
            <span className="text-xs text-zinc-500">GPT-5.2 will draft a plan to overtake the top 3.</span>
          </div>

          {analysis && (
            <div className="bento p-6 space-y-4" data-testid="comp-analysis">
              <div>
                <div className="label-tiny">Summary</div>
                <p className="font-mono text-sm leading-relaxed mt-2">{analysis.summary}</p>
              </div>
              <div>
                <div className="label-tiny mb-3">Action plan to overtake</div>
                <ol className="space-y-2">
                  {(analysis.gaps || []).map((g, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="font-mono font-bold text-[#002FA7] flex-shrink-0">0{i + 1}</span>
                      <span>{g}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
          {!analysis && analysisRaw && <pre className="bento p-4 font-mono text-xs whitespace-pre-wrap">{analysisRaw}</pre>}
        </>
      )}
    </div>
  );
}
